import { Modal, Pressable, View, Animated, Dimensions } from 'react-native'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

const SCREEN_HEIGHT = Dimensions.get('window').height

interface BottomSheetProps {
  visible: boolean
  onClose: () => void
  children: ReactNode
  backdropAccessibilityLabel: string
  height?: number
}

export function BottomSheet({ visible, onClose, children, backdropAccessibilityLabel, height }: BottomSheetProps) {
  const [modalVisible, setModalVisible] = useState(false)

  const backdropOpacity = useRef(new Animated.Value(0)).current
  const translateY = useRef(new Animated.Value(SCREEN_HEIGHT)).current

  useEffect(() => {
    if (visible) {
      setModalVisible(true)
      Animated.parallel([
        Animated.timing(backdropOpacity, {
          toValue: 0.5,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(translateY, {
          toValue: 0,
          friction: 7,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start()
    } else {
      Animated.parallel([
        Animated.timing(backdropOpacity, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: SCREEN_HEIGHT,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(() => { setModalVisible(false) })
    }
  }, [visible, backdropOpacity, translateY])

  return (
    <Modal
      visible={modalVisible}
      animationType="none"
      transparent
      onRequestClose={onClose}
    >
      <View className="flex-1 justify-end">
        <Animated.View
          className="absolute top-0 left-0 right-0 bottom-0 bg-canvas-dark"
          style={{ opacity: backdropOpacity }}
        >
          <Pressable
            style={{ flex: 1 }}
            onPress={onClose}
            accessibilityLabel={backdropAccessibilityLabel}
            accessibilityRole="button"
          />
        </Animated.View>

        <Animated.View style={{ transform: [{ translateY }], ...(height !== undefined && { height }) }}>
          {children}
        </Animated.View>
      </View>
    </Modal>
  )
}

export function DragHandle() {
  return (
    <View
      accessible={false}
      importantForAccessibility="no-hide-descendants"
      className="w-10 h-1 rounded-full bg-border self-center my-3"
    />
  )
}